import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';
import * as Updates from 'expo-updates';
import colors from '../lib/colors';
import { getCrashRecoveryCopy } from '../lib/i18n/crashRecoveryCopy';
import {
    getErrorLikeMessage,
    getErrorLikeStack,
    isKnownCefSharpObjectNotFoundWindowError,
    isKnownIntercomFrameModernWindowError,
    isKnownIntercomReduxWindowError,
    type WindowErrorDetails,
} from '../lib/sentryNoise';

const AUTO_RELOAD_COUNT_KEY = 'pz:auto-reload-count';
const AUTO_RELOAD_RESET_MS = 60_000;
const MAX_AUTO_RELOADS_PER_TAB = 1;

let crashRecoveryTriggered = false;
const crashRecoveryCopy = getCrashRecoveryCopy();

type CrashSource = 'react.boundary' | 'window.error' | 'window.unhandledrejection';

function getErrorLikeName(errorLike: unknown): string {
    const error = errorLike as { name?: unknown } | undefined;
    return typeof error?.name === 'string' ? error.name : '';
}

function getFallbackErrorMessage(errorLike: unknown): string {
    try {
        const serialized = JSON.stringify(errorLike);
        if (typeof serialized === 'string' && serialized !== '{}') {
            return serialized;
        }
    } catch {
        // fall through to the generic label below
    }

    // i18n-ignore: technical-token -- Sentry-only fallback, never rendered to users.
    return 'Unknown runtime error';
}

function toError(errorLike: unknown): Error {
    if (errorLike instanceof Error) {
        return errorLike;
    }

    if (typeof errorLike === 'string') {
        return new Error(errorLike);
    }

    const message = getErrorLikeMessage(errorLike).trim() || getFallbackErrorMessage(errorLike);
    const error = new Error(message);
    const name = getErrorLikeName(errorLike).trim();
    const stack = getErrorLikeStack(errorLike);

    if (name) {
        error.name = name;
    }

    if (stack) {
        error.stack = stack;
    }

    return error;
}

function isInterruptedMediaPlayAbort(errorLike: unknown): boolean {
    if (typeof errorLike === 'string') {
        return /aborterror/i.test(errorLike)
            && /play\(\) request was interrupted|media was removed from the document/i.test(errorLike);
    }

    const error = errorLike as { name?: unknown; message?: unknown } | undefined;
    const name = typeof error?.name === 'string' ? error.name : '';
    const message = typeof error?.message === 'string' ? error.message : '';

    if (name !== 'AbortError') {
        return false;
    }

    return /play\(\) request was interrupted|media was removed from the document/i.test(message);
}

function readAutoReloadCount(): number {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return 0;
    }

    try {
        const rawCount = window.sessionStorage.getItem(AUTO_RELOAD_COUNT_KEY);
        if (!rawCount) {
            return 0;
        }

        const parsed = Number.parseInt(rawCount, 10);
        return Number.isFinite(parsed) ? parsed : 0;
    } catch {
        return 0;
    }
}

function writeAutoReloadCount(count: number): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(AUTO_RELOAD_COUNT_KEY, String(count));
    } catch {
        // no-op (storage can fail in private modes)
    }
}

function clearAutoReloadCount(): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.removeItem(AUTO_RELOAD_COUNT_KEY);
    } catch {
        // no-op
    }
}

function captureCrash(errorLike: unknown, source: CrashSource, extra?: Record<string, unknown>): Error {
    const error = toError(errorLike);
    Sentry.captureException(error, {
        tags: {
            crash_recovery: 'enabled',
            crash_source: source,
        },
        extra,
    });
    return error;
}

function tryAutoReload(errorLike: unknown, source: CrashSource, extra?: Record<string, unknown>): boolean {
    if (__DEV__ || Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    if (isInterruptedMediaPlayAbort(errorLike)) {
        return false;
    }

    if (crashRecoveryTriggered) {
        return false;
    }

    const currentCount = readAutoReloadCount();
    if (currentCount >= MAX_AUTO_RELOADS_PER_TAB) {
        return false;
    }

    writeAutoReloadCount(currentCount + 1);
    captureCrash(errorLike, source, { ...extra, action: 'auto_reload' });

    crashRecoveryTriggered = true;
    window.setTimeout(() => {
        window.location.reload();
    }, 0);
    return true;
}

function installGlobalWebCrashHandlers(): () => void {
    if (__DEV__ || Platform.OS !== 'web' || typeof window === 'undefined') {
        return () => {};
    }

    const resetTimer = window.setTimeout(() => {
        clearAutoReloadCount();
    }, AUTO_RELOAD_RESET_MS);

    const onWindowError = (event: ErrorEvent) => {
        const errorPayload = event.error ?? event.message ?? 'Unknown window error';
        const windowErrorDetails: WindowErrorDetails = {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        };
        const windowErrorExtra = {
            windowErrorMessage: windowErrorDetails.message,
            windowErrorFilename: windowErrorDetails.filename,
            windowErrorLineno: windowErrorDetails.lineno,
            windowErrorColno: windowErrorDetails.colno,
        };

        if (
            isKnownIntercomReduxWindowError(errorPayload, windowErrorDetails) ||
            isKnownIntercomFrameModernWindowError(errorPayload, windowErrorDetails) ||
            isKnownCefSharpObjectNotFoundWindowError(errorPayload, windowErrorDetails)
        ) {
            event.preventDefault();
            return;
        }

        const didReload = tryAutoReload(
            errorPayload,
            'window.error',
            windowErrorExtra,
        );

        if (didReload || isInterruptedMediaPlayAbort(errorPayload)) {
            event.preventDefault();
            return;
        }

        captureCrash(errorPayload, 'window.error', windowErrorExtra);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
        const reason = event.reason ?? 'Unknown unhandled promise rejection';

        if (
            isKnownIntercomReduxWindowError(reason) ||
            isKnownIntercomFrameModernWindowError(reason) ||
            isKnownCefSharpObjectNotFoundWindowError(reason)
        ) {
            event.preventDefault();
            return;
        }

        const didReload = tryAutoReload(
            reason,
            'window.unhandledrejection',
        );

        if (didReload || isInterruptedMediaPlayAbort(reason)) {
            event.preventDefault();
            return;
        }

        captureCrash(reason, 'window.unhandledrejection');
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
        window.clearTimeout(resetTimer);
        window.removeEventListener('error', onWindowError);
        window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
}

type CrashRecoveryBoundaryProps = {
    children: React.ReactNode;
};

type CrashRecoveryBoundaryState = {
    hasError: boolean;
    autoReloadTriggered: boolean;
};

class CrashRecoveryBoundaryInner extends React.Component<CrashRecoveryBoundaryProps, CrashRecoveryBoundaryState> {
    state: CrashRecoveryBoundaryState = {
        hasError: false,
        autoReloadTriggered: false,
    };

    static getDerivedStateFromError(_error: Error): CrashRecoveryBoundaryState {
        return {
            hasError: true,
            autoReloadTriggered: false,
        };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        const didReload = tryAutoReload(error, 'react.boundary', {
            componentStack: info.componentStack,
        });

        if (!didReload && !isInterruptedMediaPlayAbort(error)) {
            captureCrash(error, 'react.boundary', {
                componentStack: info.componentStack,
            });
        }

        this.setState({
            hasError: true,
            autoReloadTriggered: didReload,
        });
    }

    private handleManualReload = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            clearAutoReloadCount();
            window.location.reload();
            return;
        }

        void Updates.reloadAsync().catch((error: unknown) => {
            captureCrash(error, 'react.boundary', { action: 'manual_reload_failed' });
        });
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.state.autoReloadTriggered) {
            return (
                <View style={styles.container}>
                    <Text style={styles.title}>{crashRecoveryCopy.recovering}</Text>
                </View>
            );
        }

        return (
            <View style={styles.container}>
                <View style={styles.card}>
                    <Text style={styles.title}>{crashRecoveryCopy.title}</Text>
                    <Text style={styles.description}>{crashRecoveryCopy.description}</Text>
                    <TouchableOpacity style={styles.reloadButton} onPress={this.handleManualReload} activeOpacity={0.8}>
                        <Text style={styles.reloadButtonText}>{crashRecoveryCopy.reload}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }
}

export default function CrashRecoveryBoundary({ children }: CrashRecoveryBoundaryProps) {
    React.useEffect(() => installGlobalWebCrashHandlers(), []);

    if (__DEV__) {
        return <>{children}</>;
    }

    return <CrashRecoveryBoundaryInner>{children}</CrashRecoveryBoundaryInner>;
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
    },
    card: {
        width: '100%',
        maxWidth: 420,
        borderRadius: 12,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: 20,
        paddingVertical: 24,
        gap: 12,
    },
    title: {
        fontSize: 20,
        fontWeight: '700',
        color: colors.text900,
        textAlign: 'center',
    },
    description: {
        fontSize: 14,
        lineHeight: 21,
        color: colors.text700,
        textAlign: 'center',
    },
    reloadButton: {
        marginTop: 8,
        alignSelf: 'center',
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingVertical: 12,
        paddingHorizontal: 20,
    },
    reloadButtonText: {
        color: '#ffffff',
        fontSize: 14,
        fontWeight: '600',
    },
});
